import { useNotification } from 'hooks/ui/useNotification';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { CalendarDay, downloadFile } from 'further-ui/utils';
import { format } from 'date-fns';
import base64ToBlob from 'utils/base64ToBlob';

type SelectedInvestor = {
  _id: string;
  fullName: string;
};

export function useBulkDownloadExitStatementPdf(
  exitStatementId: string,
  exitStatement: {
    submitDate?: CalendarDay;
    firm?: { firmName?: string } | null;
    companies?: Array<{ tradingName?: string | null }> | null;
  },
  selectedInvestors: Array<SelectedInvestor>,
) {
  const notification = useNotification();

  const statementDate = format(
    exitStatement?.submitDate?.toUTCDate() ?? new Date(),
    'dd-MM-yyyy',
  );

  const fileName = `${exitStatement?.firm?.firmName} - Exit Statements - ${exitStatement?.companies?.[0]?.tradingName} - ${statementDate}`;

  // Remove any character that is not a letter, number, space, bracket, & or hyphen
  // which would be invalid as a zip folder name
  const sanitisedFileName = fileName.replace(/[^a-zA-Z0-9() \-&_]/g, '');

  const bulkExportPdfExitStatements =
    api.exit.bulkExportPdfExitStatements.useMutation(
      useHandleTrpcMutation<
        ApiResponses['exit']['bulkExportPdfExitStatements']
      >()({
        onSuccess: async (data) => {
          const blob = base64ToBlob(data.file, data.mimeType);
          downloadFile(blob, data.filename || sanitisedFileName);
        },
        onError: () => {
          notification.error('Unable to download PDFs.');
        },
      }),
    );

  const download = async () => {
    await bulkExportPdfExitStatements.mutateAsync({
      exitStatementId,
      selectedInvestors,
    });
  };

  return {
    mutate: download,
    mutateAsync: download,
    isPending: bulkExportPdfExitStatements.isPending,
    isError: bulkExportPdfExitStatements.isError,
    error: bulkExportPdfExitStatements.error,
  };
}
